import { MDBMask, MDBView } from "mdbreact";
import React, { useEffect, useState } from "react";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  withRouter,
} from "react-router-dom";
import BlockUi from "./components/modules/block-ui/BlockUi";
import LoginPage from "./components/pages/login/LoginPage";
import EntryPage from "./components/pages/entry/EntryPage";
import StampCardPage from "./components/pages/stamp-card/StampCardPage";
import TenantListPage from "./components/pages/tenant-list/TenantListPage";
import TenantSelectPage from "./components/pages/tenant-select/TenantSelectPage";
import QuestionnairePage from "./components/pages/questionnaire/QuestionnairePage";
import { AppContainer } from "./containers/App";
import { StampsContainer } from "./containers/Stamps";
import { UserContainer } from "./containers/User";
import AuthGuardRoute from "./utilities/AuthGuardRoute";
import ErrorBoundary from "./utilities/ErrorBoundary";
import { ErrorContainer } from "./containers/Error";
import { CampaignContainer } from "./containers/Campaign";
import { StampCardContainer } from "./containers/StampCard";
import ContactCodeModal from "./components/modules/contact-code-modal/ContactCodeModal";
import LogoutModal from "./components/modules/logout-modal/LogoutModal";
import AccountMenu, {
  MenuElement,
} from "./components/modules/account-menu/AccountMenu";
import { Config } from "./config/Config";
import classes from "./App.module.scss";

/**
 * ページ切り替え時に画面上端にスクロールする処理
 */
const ScrollToTop: React.FC<any> = (props) => {
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return props.children;
};
/**
 * 画面上端スクロールの機能コンポーネント
 * <Scroll></Scroll> 内の子コンポーネントは、画面切り替え時に自動で上端にスクロールする。
 */
const Scroll = withRouter(ScrollToTop);

const App: React.FC = () => {
  const [isContactCodeModalOpen, setIsContactCodeModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const history = useHistory();

  const appContainer = AppContainer.useContainer();
  const loading = appContainer.context.loading;

  const errorContainer = ErrorContainer.useContainer();

  const userContainer = UserContainer.useContainer();
  const user = userContainer.user;
  const userId = user.id || "";

  const campaignContainer = CampaignContainer.useContainer();
  const campaign = campaignContainer.campaign;
  const campaignId = Config.campaignId || "";

  const stampCardContainer = StampCardContainer.useContainer();
  const stampCard = stampCardContainer.stampCard;

  if (!appContainer.context.loading) {
    if (!campaign) {
      // キャンペーンデータを初期化
      appContainer.setLoading(true);
      campaignContainer
        .initCampaign(campaignId)
        .then(() => appContainer.setLoading(false))
        .catch((error) => errorContainer.setDefaultError(userId, error));
    } else if (!stampCard && user.id && user.token) {
      // スタンプカードデータを初期化
      appContainer.setLoading(true);
      stampCardContainer
        .initStampCard(user.id, campaignId, user.token || "")
        .then(() => appContainer.setLoading(false))
        .catch((error) => errorContainer.setDefaultError(userId, error));
    }
  }

  const handleLogout = () => {
    stampCardContainer.resetStampCard();
    userContainer.logout();
    history.push("/login");
    setIsLogoutModalOpen(false);
  };

  const openExternalLink = (url?: string) => {
    if (url) window.open(url, "_blank");
  };

  const accountMenu: MenuElement[] = [
    { text: "ログアウト", onClick: () => setIsLogoutModalOpen(true) },
    {
      text: "キャンペーン詳細について",
      onClick: () => openExternalLink(campaign?.detail),
    },
  ];
  if (stampCard?.contactCode) {
    accountMenu.push({
      text: "お問い合わせはこちら",
      onClick: () => {
        setIsContactCodeModalOpen(true);
      },
    });
  }

  return (
    <StampsContainer.Provider>
      <ErrorBoundary>
        <Scroll>
          <MDBView className={classes.mainViewContainer}>
            <Switch>
              <AuthGuardRoute
                path="/entry"
                component={EntryPage}
              ></AuthGuardRoute>
              <AuthGuardRoute
                path="/stamp-card"
                component={StampCardPage}
              ></AuthGuardRoute>
              <AuthGuardRoute
                path="/tenant-select"
                component={TenantSelectPage}
              ></AuthGuardRoute>
              <AuthGuardRoute
                path="/tenant-list/:category"
                component={TenantListPage}
              ></AuthGuardRoute>
              <AuthGuardRoute
                path="/questionnaire"
                component={QuestionnairePage}
              ></AuthGuardRoute>
              <Route path="**" component={LoginPage}></Route>
            </Switch>
            {loading ? (
              <MDBMask>
                <BlockUi></BlockUi>
              </MDBMask>
            ) : null}
          </MDBView>
        </Scroll>

        {stampCard?.contactCode ? (
          <ContactCodeModal
            isOpen={isContactCodeModalOpen}
            onCloseClick={() => setIsContactCodeModalOpen(false)}
            code={stampCard.contactCode}
            contactUrl={campaign?.contactForm || ""}
          ></ContactCodeModal>
        ) : null}

        <LogoutModal
          isOpen={isLogoutModalOpen}
          onCloseClick={() => setIsLogoutModalOpen(false)}
          onLogoutClick={handleLogout}
        />

        {user.id ? <AccountMenu menuList={accountMenu}></AccountMenu> : null}
      </ErrorBoundary>
    </StampsContainer.Provider>
  );
};

export default App;
